/**
 * yetkiliGruplari.js — firma yetkililerini rol KATALOĞUNDAN türetilen gruplara ayıran
 * ortak yardımcı. Backlog #63 (backend: #62).
 *
 * Neden ortak bir modül: aynı gruplama iki ayrı sayfada gerekiyor — denetmen tarafı
 * `FirmaProfilIncelemePage.jsx` ("Firma Yetkilileri" sekmesi, yazma yetkili) ve firma
 * tarafı `FirmaKartPage.jsx` ("Firma Yetkilileri" sekmesi, salt okunur). #62'nin şartı,
 * dördüncü bir rol eklendiğinde (tek bir INSERT) hiçbir frontend değişikliği
 * gerekmemesi — bu yüzden gruplar/etiketler/sıra HİÇBİR YERDE sabit yazılmaz, hepsi
 * `GET /firma/yetkili-rolleri` (veya `profile-page.yetkiliRolleri[]`) katalogundan gelir.
 *
 * Backend'in iki tuzağı burada kapatılıyor:
 *  1. Yetkili SATIRI `grupSira` TAŞIMIYOR — yalnızca `rol/rolAd/grupKod/grupAd/sira`.
 *     Grupların sırası ancak `grupKod` üzerinden katalogla eşleştirilerek bulunabilir,
 *     yani katalog salt "boş grubu da göster" için değil, sıralama için de ZORUNLU.
 *  2. Satırlar `ORDER BY rol ASC, id ASC` geliyor, gruba/sıraya göre DEĞİL — burada
 *     yeniden sıralanıyor.
 *
 * Katalog yalnızca `aktif = 1` rolleri döner, satırlar ise emekliye ayrılmış bir rolün
 * etiketlerini taşımaya devam eder. Bu yüzden katalogda karşılığı olmayan satırlar
 * sessizce DÜŞÜRÜLMEZ, sona "katalogDisi" bir gruba toplanır.
 */
import { Stethoscope, ShieldCheck, UserRound, Users } from "lucide-react";
import i18n from "../../i18n";

// Yalnızca görsel — eşleşmeyen rol/grup jenerik ikona düşer, yani yeni bir rol ikon
// eklemeden de doğru render olur (bkz. dosya başı "sabit yazılmaz" kuralı).
const ROL_ICON = {
  isYeriHekimi: Stethoscope,
  isGuvenligiUzmani: ShieldCheck,
  firmaTemsilcisi: UserRound,
};
const GRUP_ICON = { isg: ShieldCheck, temsilci: UserRound };

/**
 * Backlog #106 (backend #105) — "Firma Yetkilisi" grubunun kodu. Etiketi veridir ("Firma
 * Temsilcisi" iken yeniden adlandırıldı) ama `kod`/`grupKod` değişmedi; ikon haritası ve
 * yazma tarafı da bunlara bakıyor.
 *
 * Bu grup iki şeyde diğerlerinden ayrılır: sekmenin en ÜSTÜNDE durur ve kişi PROFİL
 * kartıyla basılır (fotoğraf, departman, doğum tarihi, LinkedIn, biyografi, bağlı hesap).
 * Backend alanları her rol için kabul ediyor ama tasarım yalnızca bu grup için istiyor
 * (Deniz, 2026-10-01) — İSG rolleri eski sade kartta kalır.
 */
export const PROFIL_GRUP_KODU = "temsilci";

/** Satır, rol kataloğu kaydı ya da grup — `grupKod` taşıyan her şey için. */
export function profilGrubuMu(item = {}) {
  return item?.grupKod === PROFIL_GRUP_KODU;
}

/**
 * #528 (2026-10-21): İSG yetkilileri grubunun kodu (İş Yeri Hekimi, İş Güvenliği Uzmanı, İşveren İSG Görevlisi — "OHS Officers"). Yabancı
 * Katılımcı'nın Firma Yetkilileri sekmesinde bu grup çizilmez (yabancı firmadan İSG personeli istenmez).
 */
export const ISG_GRUP_KODU = "isg";

export function isgYetkiliGrubuMu(item = {}) {
  return item?.grupKod === ISG_GRUP_KODU;
}

/** Bir yetkili satırı ya da katalog rolü için ikon bileşeni. */
export function yetkiliIkonu(item = {}) {
  return ROL_ICON[item.kod ?? item.rol] ?? GRUP_ICON[item.grupKod] ?? Users;
}

/**
 * Katalog + satırlar → ekranda basılacak gruplar.
 *
 * @param {Array} kisiler  `saglikGuvenlikYetkilileri[]` (bare array ya da profile-page alanı)
 * @param {Array} roller   `yetkiliRolleri[]` / `GET /firma/yetkili-rolleri`
 * @returns {Array<{grupKod, grupAd, grupSira, roller, kisiler, katalogDisi}>}
 *          `grupSira, grupAd` sırasında, yalnızca profil grubu (bkz. PROFIL_GRUP_KODU) her
 *          zaman en başta; katalogdaki BOŞ gruplar da döner (firma yetkilisi atanmamışsa
 *          "henüz yok" kutusu basılabilsin diye).
 */
export function buildYetkiliGruplari(kisiler = [], roller = []) {
  const gruplar = new Map();

  const grubuAl = (grupKod, grupAd, grupSira, katalogDisi) => {
    const key = grupKod ?? "";
    if (!gruplar.has(key)) {
      gruplar.set(key, {
        grupKod: key,
        grupAd: grupAd || i18n.t("components:yetkiliGruplari.digerYetkililer"),
        grupSira,
        roller: [],
        kisiler: [],
        katalogDisi,
      });
    }
    return gruplar.get(key);
  };

  for (const r of roller || []) {
    grubuAl(r.grupKod, r.grupAd, r.grupSira ?? 0, false).roller.push(r);
  }

  for (const k of kisiler || []) {
    // Katalogda olmayan grup = emekliye ayrılmış ya da bilinmeyen rol. Satır görünür
    // kalmalı: birinin kaydı, rolü listeden kaldırıldı diye ekrandan silinmez.
    const grup = gruplar.has(k.grupKod ?? "")
      ? gruplar.get(k.grupKod ?? "")
      : grubuAl(k.grupKod, k.grupAd, Number.MAX_SAFE_INTEGER, true);
    grup.kisiler.push(k);
  }

  const siraya = (a, b) => (a.sira ?? 0) - (b.sira ?? 0) || (a.id ?? 0) - (b.id ?? 0);
  const out = [...gruplar.values()];
  out.forEach((g) => {
    g.roller.sort(siraya);
    g.kisiler.sort(siraya);
  });
  out.sort((a, b) => a.grupSira - b.grupSira || a.grupAd.localeCompare(b.grupAd, "tr"));
  // #106: Firma Yetkilisi kataloğun grup sırasından (isg=1, temsilci=2) bağımsız en üstte.
  // Sıra backend referans verisinde (R__firma_yetkili_rol.sql) duruyor; orası değişirse bu
  // satır gereksizleşir, zararsız kalır.
  out.sort((a, b) => Number(profilGrubuMu(b)) - Number(profilGrubuMu(a)));
  return out;
}

/**
 * Ekleme formunda seçilebilecek roller: `tekil` bir rolün kaydı zaten varsa o rol
 * listeden düşer. Backend aynı kuralı zaten 400 ile uyguluyor ("Bu firmada zaten bir
 * Firma Yetkilisi kayıtlı." — etiket rol kataloğundan geliyor), bu sadece kullanıcıyı o
 * hataya hiç sokmamak için.
 * Düzenleme sırasında satırın KENDİ rolü elenmemeli — `haricId` onu korur.
 */
export function eklenebilirRoller(roller = [], kisiler = [], haricId = null) {
  return (roller || []).filter(
    (r) => !r.tekil || !(kisiler || []).some((k) => k.rol === r.kod && k.id !== haricId),
  );
}

/**
 * Firma KENDİ kaydını yönetebilir mi? (Backlog #82 — Deniz'in kararı 2026-09-19.)
 *
 * Kural: atama belgesi OLMAYAN roller firma tarafından yönetilebilir, olanlar yalnızca
 * denetmen tarafından. Sebep: 88/89 atama belgelerini denetmen ONAYLIYOR; firma, belgede
 * adı geçen kişiyi sonradan değiştirip silebilseydi onaylı belge ile kayıt birbirini
 * tutmazdı. Bugün bu tam olarak "Firma Yetkilisi evet, İSG hayır" demek.
 *
 * Rol satırında alan `atamaFileTypeId`, yetkili satırında `atamaBelgesiFileTypeId` —
 * ikisini de kabul eder (bkz. yetkiliIkonu). Grup kodu ya da rol kodu SABİT YAZILMIYOR:
 * belgesiz yeni bir rol eklenirse firma onu da yönetebilir hale gelir, kod değişmeden.
 * Kural değişirse burası tek dokunulacak yer.
 */
// #443 (Bilge, 2026-10-18): REVERSES #82 above. A company now manages ALL its representatives, the İSG people included
// (İş Yeri Hekimi, İş Güvenliği Uzmanı, İşveren İSG Görevlisi). Why #82 held no longer holds: the people and the 88/89/99 appointment
// documents are separate things (the document group never reads the people, no gate requires them), and deleting a person keeps the
// document. The backend never enforced the old rule (#246 is withdrawn). This stays the one place to change if it ever comes back.
export function firmaYonetebilir(item = {}) {
  // WAS (until #443): return (item.atamaFileTypeId ?? item.atamaBelgesiFileTypeId) == null;
  return true;
}

/**
 * Atama belgesi olan rollerin `file_type_id → rol kodu` eşlemesi — denetmen tarafında
 * "88/89 belgesi onaylandı, belgede adı geçen kişiyi kaydet" köprüsü bunu kullanıyor.
 * Eskiden `{ 88: 'isYeriHekimi', 89: 'isGuvenligiUzmani' }` sabitiydi; artık katalogdan
 * türüyor, yani atama belgesi olan yeni bir rol de köprüye kendiliğinden katılıyor.
 * Belgesi olmayan roller (Firma Yetkilisi, `atamaFileTypeId = null`) hiç girmez.
 */
export function belgeTipiRolHaritasi(roller = []) {
  const map = {};
  for (const r of roller || []) {
    if (r.atamaFileTypeId != null) map[r.atamaFileTypeId] = r.kod;
  }
  return map;
}
